/**
 * Validates URL based on link type and portal (iimjobs/hirist)
 * @param {string} linkUrl - The URL to validate
 * @param {number} linkType - The type of link (1-4)
 * @param {string} [publishOn='iimjobs'] - The portal ('iimjobs' or 'hirist')
 * @returns {{isValid: boolean, message: string, defaultUrl: string}} - Validation result
 */
export const validateLinkUrl = (linkUrl, linkType, publishOn = 'iimjobs') => {
    const domains = {
        iimjobs: {
            showcase: process.env.REACT_APP_SHOWCASE_DOMAIN_IIMJOBS || 'showcase.iimjobs.com',
            www: 'www.iimjobs.com',
            recruiterPath: '/r',
            jobPath: '/j',
            defaultRecruiterUrl: 'https://www.iimjobs.com/registration/addAudioVideoProfile'
        },
        hirist: {
            showcase: process.env.REACT_APP_SHOWCASE_DOMAIN_HIRIST || 'showcase.hirist.tech',
            www: 'www.hirist.tech',
            recruiterPath: '/r',
            jobPath: '/j',
            defaultRecruiterUrl: 'https://www.hirist.tech/registration/addAudioVideoProfile'
        }
    };

    const portal = domains[publishOn] || domains.iimjobs; // Default to iimjobs if invalid portal
    const externalDomainRegex = new RegExp(`(${Object.values(domains).map(d => d.www.replace('www.', '')).join('|')})`, 'i');

    // Check for showcase URL (linkType 1)
    if (linkType === 1) {
        // Matches: https://showcase-node18-web.{domain}/company/{company-name-slug}-{centralisedCompanyId}.html
        const escapedDomain = portal.showcase.replace(/\./g, '\\.');
        const showcaseRegex = new RegExp(`^https:\\/\\/${escapedDomain}\\/company\\/[a-z0-9]+(?:-[a-z0-9]+)*-\\d+\\.html$`, 'i');
        if (linkUrl && !showcaseRegex.test(linkUrl)) {
            return {
                isValid: false,
                message: `The URL must be a valid ${publishOn} showcase URL (e.g., https://${portal.showcase}/company/company-name-123.html)`
            };
        }
    }
    
    // Check for job URL (linkType 2)
    if (linkType === 2) {
        const jobRegex = new RegExp(`^https:\/\/${portal.www}${portal.jobPath}\/[^/]+`);
        if (linkUrl && !jobRegex.test(linkUrl)) {
            return {
                isValid: false,
                message: `The URL must be a valid ${publishOn} job URL (e.g., https://${portal.www}${portal.jobPath}/job-id)`
            };
        }
    }
    
     // Check for recruiter profile (linkType 3)
    if (linkType === 3) {
        // If link is empty or matches the default URL, return default URL
        if (!linkUrl || linkUrl === portal.defaultRecruiterUrl) {
            return {
                isValid: true,  // Mark as valid to proceed with default URL
                defaultUrl: portal.defaultRecruiterUrl
            };
        }
        
        // If link is provided, validate it
        const recruiterRegex = new RegExp(`^https?:\/\/${portal.www}\/r\/[^/]+`);
        if (!recruiterRegex.test(linkUrl)) {
            return {
                isValid: false,
                message: `The URL must be a valid ${publishOn} recruiter profile URL (e.g., https://${portal.www}/r/username) or the default URL`,
            };
        }
    }
    
    // Check for external link (linkType 4)
    if (linkType === 4 && linkUrl) {
        // Check for iimjobs/hirist domains in any part of the URL
        const blockedDomains = ['iimjobs.com', 'hirist.tech', 'hirist.com'];
        const domainMatch = linkUrl.match(/https?:\/\/(?:www\.)?([^\/]+)/i);
        const domain = domainMatch ? domainMatch[1].toLowerCase() : '';
        
        // Check if the domain contains any blocked domain
        const isBlockedDomain = blockedDomains.some(blocked => 
            domain.includes(blocked) || 
            blocked.includes(domain.replace('www.', ''))
        );

        if (isBlockedDomain) {
            return {
                isValid: false,
                message: "In case of External Link, URL can't be of iimjobs or hirist domain. Please add the correct URL that redirects to a different website or change Link Type."
            };
        }
    }
    
    return { isValid: true };
};

// Helper object for friendly names in error messages
const publishToFriendlyName = {
    iimjobs: 'IIMJobs',
    hirist: 'Hirist'
};

export default {
    validateLinkUrl
};
